import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  Box,
  Paper,
  TableCell,
  Checkbox,
  Container,
  Typography,
  Grid,
  Chip,
} from "@mui/material";
import DataTable, {
  IHeadCell,
  IDataTableRow,
} from "components/Common/DataTable";
import {
  selectZNSNotification,
  selectTotalCount,
  selectPageCount,
  loadZNSNotification,
  INotificationPayload,
  IMessage,
  exportZNSNotification,
} from "modules/notification/store/notificationSlice";
import NotificationService from "modules/notification/services/notificationService";
import Loading from "components/Common/Loading";
import { ConfirmModal } from "components/Common";
import Statistics from "../common/MessageStatistics";
import MessageToolbar from "../common/MessageToolbar";
import moment from "moment";

/** SortBy field names accepted by api/Notification/v1/messages — must match IHeadCell.id for sortable columns */
const SORTABLE_COLUMN_IDS = new Set([
  "ModifiedDate",
  "CreatedDate",
  "ZnsCampaignName",
  "ZnsTemplateName",
  "ZnsTemplateTag",
  "Status",
]);

const headCells: IHeadCell[] = [
  {
    id: "phoneNumber",
    numeric: false,
    disablePadding: false,
    label: "Phone Number",
    minWidth: 150,
    sortable: false,
  },
  {
    id: "messageId",
    numeric: false,
    disablePadding: false,
    label: "Message ID",
    minWidth: 250,
    sortable: false,
  },
  {
    id: "znsCampaignType",
    numeric: false,
    disablePadding: false,
    label: "ZNS Campaign Type",
    minWidth: 140,
    sortable: false,
  },
  {
    id: "ZnsTemplateTag",
    numeric: false,
    disablePadding: false,
    label: "ZNS Purpose",
    minWidth: 150,
  },
  {
    id: "ZnsTemplateName",
    numeric: false,
    disablePadding: false,
    label: "Template Name",
    minWidth: 200,
  },
  {
    id: "ZnsCampaignName",
    numeric: false,
    disablePadding: false,
    label: "ZNS Campaign",
    minWidth: 250,
  },
  {
    id: "templateId",
    numeric: false,
    disablePadding: false,
    label: "Template ID",
    minWidth: 150,
    sortable: false,
  },
  {
    id: "recipient",
    numeric: false,
    disablePadding: false,
    label: "Recipient",
    minWidth: 150,
    sortable: false,
  },
  {
    id: "sentTime",
    numeric: false,
    disablePadding: false,
    label: "Sent Time",
    minWidth: 180,
    sortable: false,
  },
  {
    id: "Status",
    numeric: false,
    disablePadding: false,
    label: "Status",
    minWidth: 120,
    sortable: true,
  },
  {
    id: "errorCode",
    numeric: false,
    disablePadding: false,
    label: "Error Code",
    minWidth: 120,
    sortable: false,
  },
  {
    id: "msgError",
    numeric: false,
    disablePadding: false,
    label: "MsgError",
    minWidth: 250,
    sortable: false,
  },
  {
    id: "params",
    numeric: false,
    disablePadding: false,
    label: "Params",
    minWidth: 300,
    sortable: false,
  },
];

function formatRowDate(value: string | undefined) {
  return value ? moment(value).format("YYYY/MM/DD HH:mm:ss") : "-";
}

function StatusChip({ status }: { status: string }) {
  const convertStatus = {
    Pending: "Pending",
    Sent: "Sent",
    Failed: "Failed",
    Invalid: "Invalid",
    Error: "Error",
  };

  const statusColor = {
    Pending: "warning.main",
    Sent: "success.main",
    Failed: "error.main",
    Invalid: "error.main",
    Error: "error.main",
  };

  return (
    <Chip
      label={convertStatus[status as keyof typeof convertStatus]}
      variant="outlined"
      size="small"
      sx={{
        borderRadius: "16px",
        height: "28px",
        fontWeight: 500,
        color:
          statusColor[status as keyof typeof statusColor] || "text.secondary",
        borderColor:
          statusColor[status as keyof typeof statusColor] || "grey.400",
        backgroundColor: "transparent",
        "& .MuiChip-label": {
          px: 2,
        },
      }}
    />
  );
}

function UserRow(props: IDataTableRow) {
  const item = props.item as IMessage | undefined;
  if (!item) return null;

  return (
    <>
      <TableCell>{item.phoneNumber as string}</TableCell>
      <TableCell>{item.notificationMessageId as string}</TableCell>
      <TableCell>{item.znsCampaignType as string}</TableCell>
      <TableCell>{item.znsTemplateTag as string}</TableCell>
      <TableCell>{item.znsTemplateName as string}</TableCell>
      <TableCell>{item.znsCampaignName as string}</TableCell>
      <TableCell>{item.template as string}</TableCell>
      <TableCell>{item.customerName as string}</TableCell>
      <TableCell>{formatRowDate(item.lastSendAttemptDate as string)}</TableCell>
      <TableCell>
        <StatusChip status={item.status as string} />
      </TableCell>
      <TableCell>{item.errorCode as string}</TableCell>
      <TableCell>{item.lastSendError as string}</TableCell>
      <TableCell>{item.messageContent as string}</TableCell>
    </>
  );
}

const DEFAULT_FILTERS: INotificationPayload = {
  SearchTerm: "",
  ZnsTemplateTag: undefined,
  ZnsCampaignId: undefined,
  Status: undefined,
  CreatedDateFrom: "",
  SortDirection: "DESC",
};

export default function OutboundMesssageOverviewPage() {
  const dispatch = useAppDispatch();

  const ZNSNotification = useAppSelector(selectZNSNotification);
  const ZNSNotificationPageCount = useAppSelector(selectPageCount);
  const ZNSNotificationTotalCount = useAppSelector(selectTotalCount);

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [filters, setFilters] = useState<INotificationPayload>(DEFAULT_FILTERS);
  const [paging, setPagingData] = useState({
    total: 0,
    pages: 1,
    page: 1,
    limit: 15,
  });

  const getZNSNotification = useCallback(() => {
    dispatch(
      loadZNSNotification({
        ...filters,
        PageNumber: paging.page,
        PageSize: paging.limit,
      }),
    );
  }, [dispatch, paging.page, paging.limit, filters]);

  const handleFilterChange = (newFilters: Partial<INotificationPayload>) => {
    setFilters((prev: INotificationPayload) => ({ ...prev, ...newFilters }));
    setPagingData((prev) => ({ ...prev, page: 1 }));
  };

  const handleSortChange = (property: string, direction: "asc" | "desc") => {
    if (!SORTABLE_COLUMN_IDS.has(property)) {
      return;
    }
    handleFilterChange({
      SortBy: property as NonNullable<INotificationPayload["SortBy"]>,
      SortDirection: direction === "asc" ? "ASC" : "DESC",
    });
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPagingData((prev) => ({ ...prev, page: 1 }));
  };

  const onPageChange = (page: number) => {
    setPagingData({
      ...paging,
      page,
    });
  };

  const confirmModal = () => {
    setOpenConfirmModal(false);
  };

  const resendNotificationMessages = (data: any) => {
    if (data) {
      NotificationService.resendNotificationMessages(data)
        .then(() => {
          setConfirmMessage("Resend successfully !!!");
          setOpenConfirmModal(true);
          getZNSNotification();
        })
        .catch((error) => {
          setConfirmMessage(
            error?.Message || "System error, please try again later",
          );
          setOpenConfirmModal(true);
        });
    } else {
      setConfirmMessage("System error, please try again later");
      setOpenConfirmModal(true);
    }
  };

  useEffect(() => {
    getZNSNotification();
  }, [getZNSNotification]);

  useEffect(() => {
    const pageData = {
      ...paging,
      total: ZNSNotification?.totalCount || 0,
    };

    setPagingData({
      ...pageData,
      pages: Math.ceil(pageData.total / pageData.limit),
      page:
        ZNSNotificationPageCount === 0 && pageData.page > 1
          ? pageData.page - 1
          : pageData.page,
    });
  }, [ZNSNotificationTotalCount]);

  const handleExport = () => {
    if (!ZNSNotification) return;
    dispatch(
      exportZNSNotification({
        ...filters,
        PageNumber: 1,
        PageSize: ZNSNotification?.totalSent,
      }),
    );
  };

  return (
    <Container maxWidth={false}>
      <>
        <Statistics
          isLoading={!ZNSNotification}
          totalSent={ZNSNotification?.totalSent || 0}
          success={ZNSNotification?.totalSuccess || 0}
          failed={ZNSNotification?.totalFailed || 0}
        />
      </>
      <Box sx={{ width: "100%", mt: 3 }}>
        <Paper sx={{ width: "100%", mb: 2, overflow: "hidden" }}>
          <MessageToolbar
            title={"Lịch sử tin nhắn"}
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            onExport={handleExport}
          />
          {!ZNSNotification ? (
            <Loading />
          ) : (
            <DataTable
              hideToolbar={true}
              fromPage="notification"
              headCells={headCells}
              rows={ZNSNotification?.results || []}
              paging={paging}
              name="notificationMessageId"
              title={""}
              expandablePagination={true}
              sortable={true}
              order={filters.SortDirection === "ASC" ? "asc" : "desc"}
              orderBy={filters.SortBy ?? ""}
              onSortChange={handleSortChange}
              onPageChange={onPageChange}
              onRowsPerPageChange={() => {}}
              onResendNotification={resendNotificationMessages}
              maxHeight={"calc(100vh - 440px)"}
              stickyHeader={true}
            >
              <UserRow />
            </DataTable>
          )}
        </Paper>
      </Box>

      <ConfirmModal
        open={openConfirmModal}
        setOpen={setOpenConfirmModal}
        content={confirmMessage}
        onConfirm={confirmModal}
        backdropClick={true}
      />
    </Container>
  );
}
